import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { usePersonalization } from "@/lib/personalization";
import RealStories from "./RealStories";
import { GuideCta } from "@/components/GuideCta";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const KARIN = "/img/karin-eckhard-portrait.jpg";

export default function WhyUs() {
  const { section } = useSiteContent();
  const c = section("why_us", HOME.why_us);
  const { pick } = usePersonalization();
  const VARIANT = { "Local knowledge": "local_knowledge", "Practical coordination": "practical_coordination" };
  const base = Array.isArray(c.reasons) && c.reasons.length ? c.reasons : HOME.why_us.reasons;
  const reasons = base.map((r) => (VARIANT[r.title] && pick(VARIANT[r.title]) ? { ...r, text: pick(VARIANT[r.title]) } : r));

  return (
    <section id="why-us" data-testid="why-us-section" className="bg-[#F5EFE7] text-[#451C25] py-24 md:py-32 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7">
            <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase mb-6 font-semibold" style={K}>{c.eyebrow}</p>
            <h2 data-testid="why-us-headline" className="text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.headline}</h2>
            <p data-testid="why-us-p1" className="mt-8 text-lg md:text-xl leading-relaxed" style={S}>{c.p1}</p>
            <p className="mt-4 text-[#6B4954] text-base md:text-lg leading-relaxed" style={S}>{c.p2}</p>
            <p className="mt-12 text-[#897756] text-[10px] tracking-[0.26em] uppercase font-semibold" style={K}>{c.reasons_eyebrow}</p>
            <ul data-testid="why-us-reasons" className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
              {reasons.map((r, i) => (
                <li key={i} data-testid={`why-us-reason-${i}`} className="border-t border-[#B3A17D]/60 pt-4">
                  <h3 className="text-lg md:text-xl leading-snug" style={{ ...K, fontWeight: 500 }}>{r.title}</h3>
                  <p className="mt-2 text-[#6B4954] text-[15px] leading-relaxed" style={S}>{r.text}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Karin: the person behind the service */}
          <div data-testid="why-us-founder" className="lg:col-span-5 lg:pt-2">
            <div className="relative">
              <img
                data-testid="why-us-founder-photo"
                src={KARIN}
                alt="Karin Eckhard, Espíritu Travel and Destino Europe"
                loading="lazy"
                className="w-full aspect-[4/5] lg:aspect-square object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 bg-[#451C25] text-[#F5EFE7] px-5 py-3 text-[11px] tracking-[0.04em]" style={K}>
                <span className="uppercase tracking-[0.22em] text-[10px]">Karin Eckhard</span> · Espíritu Travel
              </div>
            </div>
            <div className="bg-[#451C25] text-[#F5EFE7] p-8 md:p-10">
              <p className="text-[#B3A17D] text-[10px] tracking-[0.26em] uppercase font-semibold" style={K}>{c.founder_eyebrow}</p>
              <p data-testid="why-us-founder-text" className="mt-4 text-[17px] md:text-lg leading-relaxed" style={S}>{c.founder_text}</p>
              <Link
                to="/about"
                data-testid="why-us-about-link"
                className="mt-7 inline-flex items-center gap-2 text-[#B3A17D] text-[12px] tracking-[0.18em] uppercase font-semibold border-b border-[#B3A17D] pb-0.5 hover:text-[#F5EFE7] hover:border-[#F5EFE7] transition-colors"
                style={K}
              >
                {c.founder_link} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
        <RealStories />
        <WhyUsCta />
      </div>
    </section>
  );
}

function WhyUsCta() {
  const { section } = useSiteContent();
  const ctas = section("conversion_ctas", HOME.conversion_ctas);
  return (
    <div data-testid="why-us-cta" className="mt-16 md:mt-20 pt-10 border-t border-[#451C25]/15 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      <div className="lg:col-span-7">
        <p className="text-2xl md:text-3xl leading-tight text-[#451C25]" style={{ fontFamily: "Kanit, Arial, sans-serif", fontWeight: 500 }}>{ctas.why_us_cta_heading}</p>
        <div className="mt-5"><ScheduleCallButton source="why_us" tone="burgundy" label="Schedule a Free Call" /></div>
        <p className="mt-3 text-[#6B4954] text-[12px] tracking-[0.12em] uppercase" style={{ fontFamily: "Kanit, Arial, sans-serif" }}>{ctas.call_meta}</p>
      </div>
      <GuideCta placement="why_us" className="lg:col-span-4 lg:col-start-9" />
    </div>
  );
}
